import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { services } from "@/data/servicesData";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services", dropdown: true },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About Us" },
  { to: "/careers", label: "Careers" },
  { to: "/resources", label: "Resources" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/logo-horizontal.png" alt="ClariDent RCM — We clear the way. You care for smiles." className="h-11 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-6" data-testid="nav-links">
          {links.map((l) =>
            l.dropdown ? (
              <div key={l.label} className="relative group">
                <Link
                  to={l.to}
                  data-testid="nav-link-services"
                  className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-200 hover:text-teal-600 ${
                    location.pathname.startsWith("/services") ? "text-teal-600" : "text-navy-900"
                  }`}
                >
                  {l.label} <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" data-testid="services-dropdown">
                  <div className="w-72 rounded-2xl border border-slate-200 bg-white shadow-xl p-3">
                    {services.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-dropdown-${s.slug}`}
                        className="block px-4 py-2.5 rounded-xl text-sm font-semibold text-navy-900 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                      >
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-semibold transition-colors duration-200 hover:text-teal-600 ${
                  location.pathname === l.to ? "text-teal-600" : "text-navy-900"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
          <Link
            to="/book"
            data-testid="nav-free-audit-btn"
            className="rounded-full bg-teal-600 px-6 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-0.5 hover:shadow-lg"
          >
            Free RCM Audit
          </Link>
        </nav>
        <button
          className="lg:hidden text-navy-900"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-base font-semibold text-navy-900 hover:text-teal-600"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/book"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-audit-btn"
            className="rounded-full bg-teal-600 px-6 py-3 text-center text-sm font-bold text-white"
          >
            Free RCM Audit
          </Link>
        </div>
      )}
    </header>
  );
}
